import { AuthState, OktaAuthOptions } from '@okta/okta-auth-js'
import Environment from 'common/types/Environment'
import getEnv from '../common/helpers/getEnv'

const ENV = getEnv()

function getClientId() {
  switch (ENV) {
    case Environment.Production:
      return process.env.REACT_APP_OIDC_CLIENT_ID_PRODUCTION
    case Environment.Sandbox:
      return process.env.REACT_APP_OIDC_CLIENT_ID_SANDBOX
    case Environment.Staging:
      return process.env.REACT_APP_OIDC_CLIENT_ID_STAGING
    case Environment.QA:
      return process.env.REACT_APP_OIDC_CLIENT_ID_QA
    default:
      return process.env.REACT_APP_OIDC_CLIENT_ID_SANDBOX
  }
}

const AUTH_PROVIDERS = ['okta', 'jumpcloud'] as const

export type AuthProvider = typeof AUTH_PROVIDERS[number]

function getProvidersListedInDevAuth(): string[] {
  return (process.env.REACT_APP_DEV_AUTH || '')
    .split(',')
    .map(listedProvider => listedProvider.trim())
}

function getProvidersRequestedForEnvironment(): readonly AuthProvider[] {
  if (ENV === Environment.Test) return []
  if (ENV === Environment.Dev) {
    const providersListedInDevAuth = getProvidersListedInDevAuth()
    return AUTH_PROVIDERS.filter(provider => providersListedInDevAuth.includes(provider))
  }
  return AUTH_PROVIDERS
}

function isProviderConfigured(provider: AuthProvider): boolean {
  if (provider === 'jumpcloud') return Boolean(getJumpCloudClientId())
  return true
}

export function getEnabledProviders(): AuthProvider[] {
  return getProvidersRequestedForEnvironment().filter(isProviderConfigured)
}

export function isAuthEnabled(): boolean {
  return getEnabledProviders().length > 0
}

function getJumpCloudClientId() {
  switch (ENV) {
    case Environment.Production:
      return process.env.REACT_APP_JUMPCLOUD_CLIENT_ID_PRODUCTION
    case Environment.Sandbox:
      return process.env.REACT_APP_JUMPCLOUD_CLIENT_ID_SANDBOX
    case Environment.Staging:
      return process.env.REACT_APP_JUMPCLOUD_CLIENT_ID_STAGING
    case Environment.QA:
      return process.env.REACT_APP_JUMPCLOUD_CLIENT_ID_QA
    case Environment.Dev:
      return process.env.REACT_APP_JUMPCLOUD_CLIENT_ID_DEV
    default:
      return undefined
  }
}

const CLIENT_ID = getClientId()
const ISSUER = 'https://loadsmart.okta.com'
const OKTA_TESTING_DISABLEHTTPSCHECK = false
const REDIRECT_URI = `${window.location.origin}/oidc/login/callback`

// The trailing slash is part of JumpCloud's issuer: its tokens carry it in `iss`.
const JUMPCLOUD_ISSUER = 'https://oauth.id.jumpcloud.com/'

export const jumpcloudConfig = {
  authority: JUMPCLOUD_ISSUER,
  clientId: getJumpCloudClientId() || '',
  redirectUri: REDIRECT_URI,
  scope: 'openid email profile offline_access',
}

const config: { oidc: OktaAuthOptions & { disableHttpsCheck: any } } = {
  oidc: {
    clientId: CLIENT_ID,
    issuer: ISSUER,
    redirectUri: REDIRECT_URI,
    devMode: [Environment.Sandbox, Environment.Dev, Environment.Test].includes(ENV),
    scopes: ['openid', 'email', 'offline_access'],
    pkce: true,
    disableHttpsCheck: OKTA_TESTING_DISABLEHTTPSCHECK,
    services: {
      autoRenew: true,
      autoRemove: true,
      syncStorage: true,
    },
    tokenManager: {
      expireEarlySeconds: 15,
      autoRemove: true,
      autoRenew: true,
      secure: true,
    },
    transformAuthState: async (authInstance: any, authState: AuthState) => {
      if (!isAuthEnabled()) {
        return {
          ...authState,
          isAuthenticated: true,
          idToken: {
            idToken: 'test',
            claims: {
              sub: 'test',
            },
            issuer: 'test',
            clientId: 'test',
            expiresAt: 2147483647,
            authorizeUrl: 'test',
            scopes: [],
          },
          accessToken: {
            accessToken: 'test',
            tokenType: 'test',
            userinfoUrl: 'test',
            claims: {
              sub: 'test',
            },
            issuer: 'test',
            clientId: 'test',
            expiresAt: 2147483647,
            authorizeUrl: 'test',
            scopes: [],
          },
        }
      }

      return authState
    },
  },
}

export default config
